// Copyright ©️ 2026 eVolve MEP, LLC

import { type Facility, type Project, useEvolvePost, type WorkOrderPaginated } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { FormModal } from 'components/Mantine/Modals/FormModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  BendElementDetailsForm,
  type BendFormBendData,
} from 'modules/Shop/EvolveFabrication/BendElementDetails/BendElementDetailsForm';

type BaseProps = {
  onClose: () => void;
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

// RequireAtLeastOne doesn't seem friendly with other type helpers,
// So I can't use WithRequiredNotNull<Props, 'bendData'> here like I'd like.
// So instead I'll just use a base type and add two types with `bendData` having different nullability.
type FormProps = BaseProps & { bendData: BendFormBendData };

const CreateBendElementDetailsForm = ({ bendData, onClose, onBendsCreated, ...props }: FormProps) => {
  const { apiCall: createBends } = useEvolvePost('shop/fabrication/bendCreator');
  return (
    <BendElementDetailsForm
      onSubmit={async ({ workOrder, ...elementData }) => {
        if (isNil(workOrder)) return;
        await createBends({
          workOrderId: workOrder.workOrderId,
          ...bendData,
          ...elementData,
        });
        notifications.show({
          title: 'Successfully added bends',
          message: `Added ${bendData.quantity} part${bendData.quantity === 1 ? '' : 's'} to ${workOrder.name}.`,
          color: 'green',
        });
        onBendsCreated();
        onClose();
      }}
      {...props}
    />
  );
};

type ModalProps = BaseProps & { bendData: BendFormBendData | null };

export const CreateBendElementDetailsModal = ({ bendData, ...props }: ModalProps) => (
  <FormModal
    title="Add bends to work order"
    type="submit"
    opened={bendData !== null}
    onClose={props.onClose}
    startDirty
  >
    {isNotNil(bendData) && <CreateBendElementDetailsForm bendData={bendData} {...props} />}
  </FormModal>
);
